import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import type {
  ContentDatabase,
  ContentDatabaseHandle,
} from "@-/dev.improve.haniverse.database.content";
import type { FindManyConfig } from "@-/dev.improve.haniverse.lib.database";
import type { ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import { hashKey, type QueryFunction } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useCallback } from "react";
import { stripRelationalConfigColumns } from "#strip-relational-config-columns";
import type { ContentColumnsAgnosticConfig, ContentQueryTable, ContentRelationalRow } from "#types";

// The content database is immutable read-only info data — it never changes
// within a session, so (unlike the actor queries) there is no change-version
// subscription. `staleTime: Infinity` makes exact-same reads in quick succession
// resolve from cache instead of re-hitting disk; a finite `gcTime` bounds memory
// so inactive entries are garbage-collected rather than growing forever.
const CONTENT_QUERY_STALE_TIME = Number.POSITIVE_INFINITY;
const CONTENT_QUERY_GC_TIME = 5 * 60 * 1000;

// `location` is the caller's `import.meta.location`, threaded in via
// `contentQueries(import.meta.location)`, so `db(location)` attributes query
// logs to the read's origin instead of this factory file. `null` opts out.
export function createContentFindManyQuery<TTable extends ContentQueryTable>(
  location: ImportMetaLocation,
  table: TTable,
) {
  type FindManyQueryConfig = FindManyConfig<ContentDatabaseHandle, TTable>;
  // The joined row for a specific caller config `Cfg`, reconstructed from the
  // content schema's relational config so `config.with` relations are reflected
  // in the type.
  type RowFor<Cfg extends true | Record<string, unknown>> = ContentRelationalRow<TTable, Cfg>;

  type FindManyQueryKey<
    WithConfig extends FindManyQueryConfig & ContentColumnsAgnosticConfig,
    Config extends FindManyQueryConfig & ContentColumnsAgnosticConfig,
  > = readonly ["contentQuery", TTable, "findMany", WithConfig, Config, { db: ContentDatabase }];

  // `useGetQueryKey` binds the database once (at the top level of a hook) from
  // `withConfig`, then the returned getter builds a key from a per-lookup
  // `config` supplied later — inside a callback, for many different `where`s.
  // That late binding lets prefetch/mutation code capture a static `with` at
  // render while keying each individual read at fetch time. (The content DB is
  // immutable, so there is no change-version to subscribe to.)
  //
  // Both configs are column-stripped HERE, the single choke point every key
  // flows through: the query functions read their fetch config back off the key,
  // so stripping at key-build time makes cache entries column-agnostic.
  function useGetQueryKey<
    const WithConfig extends FindManyQueryConfig & ContentColumnsAgnosticConfig,
  >(withConfig: WithConfig) {
    const [db] = useContextSelectors(ContentDatabaseContext, (context) => context.db);
    const strippedWithConfig = stripRelationalConfigColumns(withConfig);
    const strippedWithConfigHash = hashKey([strippedWithConfig]);

    return useCallback(
      <const Config extends FindManyQueryConfig & ContentColumnsAgnosticConfig>(
        config: Config,
      ): FindManyQueryKey<WithConfig, Config> =>
        [
          "contentQuery",
          table,
          "findMany",
          strippedWithConfig,
          stripRelationalConfigColumns(config),
          { db },
        ] as const,
      // The hash is the query cache's definition of config equality. Depending
      // on it keeps inline equivalent configs stable despite stripping creating
      // a fresh object on every render.
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- the hash stands in for strippedWithConfig
      [db, strippedWithConfigHash],
    );
  }

  async function queryFn({
    queryKey,
  }: {
    queryKey: ReturnType<ReturnType<typeof useGetQueryKey>>;
    // The pair form reads its config at runtime from the key, so its config type
    // isn't statically known — resolve to the base (relation-less) row.
  }): Promise<RowFor<Record<string, never>>[]> {
    const withConfig = queryKey[3];
    const config = queryKey[4];
    const { db } = queryKey[5];
    const queryTable = db(location).query[table];
    // `table` and `config` are paired in the query key; drizzle's per-table
    // `KnownKeysOnly` generic cannot be recovered through the factory parameter.
    /* eslint-disable typescript/no-unsafe-type-assertion -- see above */
    return queryTable.findMany({
      ...withConfig,
      ...config,
    } as Parameters<typeof queryTable.findMany>[0]) as unknown as Promise<
      RowFor<Record<string, never>>[]
    >;
    /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */
  }

  /**
   * Builds query options from a render-bound key getter and a lookup-specific
   * config. `useGetQueryKey` captures the database and the static relation shape
   * once; this pure function is safe to call later from navigator/prefetch
   * callbacks. Carries the immutable-DB cache knobs (`staleTime`/`gcTime`).
   */
  function getQueryOptions<
    const WithConfig extends FindManyQueryConfig & ContentColumnsAgnosticConfig,
    const Config extends FindManyQueryConfig & ContentColumnsAgnosticConfig,
  >(getQueryKey: (config: Config) => FindManyQueryKey<WithConfig, Config>, config: Config) {
    const queryKey = getQueryKey(config);
    const typedQueryFn: QueryFunction<RowFor<WithConfig & Config>[], typeof queryKey> = async ({
      queryKey: queryFnQueryKey,
    }) =>
      // The key getter carries the precise static `with` and lookup config types.
      // `queryFn` intentionally exposes the relation-agnostic base type because
      // it can also be called directly without a typed options builder.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- refined by the paired key getter above
      (await queryFn({ queryKey: queryFnQueryKey })) as RowFor<WithConfig & Config>[];

    return {
      gcTime: CONTENT_QUERY_GC_TIME,
      queryFn: typedQueryFn,
      queryKey,
      staleTime: CONTENT_QUERY_STALE_TIME,
    };
  }

  // Single-shot entry point. Capturing the literal `Cfg` lets the return type
  // reflect `Cfg["with"]` joins. The split pair above remains for prefetch and
  // composition callers that must defer `where`.
  function useQueryOptions<const Cfg extends FindManyQueryConfig & ContentColumnsAgnosticConfig>(
    config: Cfg,
  ) {
    const getQueryKey = useGetQueryKey(config);
    const { queryFn: typedQueryFn } = getQueryOptions(getQueryKey, config);

    return {
      gcTime: CONTENT_QUERY_GC_TIME,
      queryFn: typedQueryFn,
      get queryKey() {
        return getQueryKey(config);
      },
      staleTime: CONTENT_QUERY_STALE_TIME,
    };
  }

  return { getQueryOptions, queryFn, useGetQueryKey, useQueryOptions };
}
